// Banked Pass 14: this file's four `any` fields (`apiResponseRaw`,
// `orderStatus`, `paymentMethod`, `asset`) are load-bearing across the
// property listing UI — tightening cascades into propertyListUtils,
// usePropertyListData, and ListViewCell. Defer the structural shape work
// to a future pass; suppress the rule at the file scope until then.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { ComponentData } from '@/types/table';

export type PropertyTable = {
  id: string;
  apiResponseRaw: any;
  propertyAddress: ComponentData;
  lastOrderDate: ComponentData;
  age: ComponentData;
  propertyStatus: ComponentData;
  isActive: string;
  isListed: string;
  daysOnMarket: string;
  assignee: string;
  portfolioName: string;
  hasPhotos: string;
  has3DTour: string;
  noticeGivenDate: string | null;
  moveOutDate: string | null;
  propertySource: string;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  rentReady: string | null;
  availableDate: string | null;
  addToCart?: ComponentData;
  recentAssets?: ComponentData;
  moreOption?: ComponentData;
  orderNumber: ComponentData;
  orderDate: string | null;
  servicesOrdered: ComponentData;
  'appointments.dateStartLocal': string;
  orderCompletedDate: string;
  orderStatus: any;
  clientPropertyId: string;
  clientUnitId: string;
  unableToComplete: string;
  unableToCompleteReason: string;
  unableToCompleteNotes: ComponentData;
  accessType: string;
  onsiteContactName: string;
  firstAvailableDate: string;
  entity: string;
  gateCode: string;
  lockboxLocation: string;
  downloadInvoice: string;
  orderedBy: string;
  orderMethod: string;
  paymentMethod: any;
  orderNotes: ComponentData;
  rescheduleForUTCs: string;
  orderAmount: ComponentData;
  listingDate: string | null;
  propertyType: string;
  tenantStatus: string;
  asset: any;
  formattedAddress?: string;
  hasPhotoset?: boolean;
};

export type TableFilter = {
  address?: string | null;
  fullAddress?: string | null;
  status?: string | null;
  orderStatus?: string | string[] | null;
  zipCode?: string | null;
  orderDateFrom?: string | null;
  orderDateTo?: string | null;
  entityUUIDs?: string[] | null;
  activeStatus?: string | null;
};

// ---------------------------------------------------------------------------
// Tone palette — shared by PropertyCard's order strip and PropertyDetailDrawer's
// leasing-status pill. Keep additions/changes here; both consumers import.
// ---------------------------------------------------------------------------

export type StatusTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'destructive'
  | 'neutral';

/**
 * Map an active orderStatus or leaseStatus string to a soft-surface tone.
 * Keys are normalized lowercase + non-alphanumerics stripped, so we tolerate
 * "Appointment Complete" / "appointmentcomplete" / "AppointmentComplete" alike.
 */
export const statusToTone = (status: string | null | undefined): StatusTone => {
  if (!status) return 'neutral';
  const norm = status.toLowerCase().replace(/[\s_-]/g, '');
  if (norm === 'delivered' || norm === 'verified' || norm === 'rented') {
    return 'success';
  }
  if (
    norm === 'scheduled' ||
    norm === 'processing' ||
    norm === 'processed' ||
    norm === 'appointmentcomplete' ||
    norm === 'occupied'
  ) {
    return 'info';
  }
  if (norm === 'unabletocomplete' || norm === 'noticegiven' || norm === 'vacant') {
    return 'warning';
  }
  if (norm === 'cancelled' || norm === 'canceled' || norm === 'refunded') {
    return 'destructive';
  }
  return 'neutral';
};

export const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-success-soft text-success-soft-foreground border-success/30',
  info: 'bg-info-soft text-info-soft-foreground border-info/30',
  warning: 'bg-warning-soft text-warning-soft-foreground border-warning/30',
  destructive:
    'bg-destructive-soft text-destructive-soft-foreground border-destructive/30',
  neutral: 'bg-surface-inset text-muted-foreground border-border',
};

// ---------------------------------------------------------------------------
// List-view address cell — renders the address as a button when an onOpen
// handler is provided. Used by usePropertyListData.getColumns() so the
// list view can open the property-detail drawer from the address cell.
// Mirrors the team-members memberNameCell pattern from the 2026-05-09 walks
// session.
//
// Note: React.createElement is used here instead of JSX because this file has
// a .ts extension. JSX syntax is only permitted in .tsx files. Renaming to
// .tsx would require updating 16 consumer import paths — not worth the churn.
// ---------------------------------------------------------------------------

export const buildAddressCell = (
  item: PropertyTable,
  onOpen: (item: PropertyTable) => void,
  formattedAddress: string,
): ComponentData => {
  const [line1, ...rest] = formattedAddress.split('\n').filter(Boolean);
  const restNode =
    rest.length > 0
      ? React.createElement(
          'div',
          { className: 'text-sm text-muted-foreground' },
          rest.join(' · '),
        )
      : null;
  const button = React.createElement(
    'button',
    {
      type: 'button',
      onClick: () => onOpen(item),
      className:
        'text-left hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 rounded-sm',
    },
    React.createElement('div', null, line1),
    restNode,
  );
  return { component: button, data: formattedAddress };
};
